import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { Stethoscope, ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";

const STATUS_META = {
  open: { label: "Awaiting reviews", tone: "bg-muted text-foreground" },
  psychologist_submitted: { label: "Psychiatrist pending", tone: "bg-secondary/30 text-primary" },
  psychiatrist_submitted: { label: "Psychologist pending", tone: "bg-secondary/30 text-primary" },
  tie_break_needed: { label: "Tie-break needed", tone: "bg-destructive/10 text-destructive" },
  finalized: { label: "Finalized", tone: "bg-primary/10 text-primary border border-primary/30" },
};

export default function ClinicianDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [cases, setCases] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/admin/login"); return; }
    if (!["PSYCHOLOGIST", "PSYCHIATRIST"].includes(user.role)) {
      if (["PLATFORM_ADMIN", "CONTENT_ADMIN"].includes(user.role)) { nav("/admin"); return; }
      nav("/");
      return;
    }
    api.get("/clinician/cases")
      .then((r) => setCases(r.data))
      .catch(() => toast.error("Could not load your case queue"))
      .finally(() => setFetching(false));
  }, [user, loading, nav]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background">
        <BrandHeader />
        <div className="mano-container py-20 text-muted-foreground">Loading…</div>
      </div>
    );
  }

  const pending = cases.filter((c) => !c.my_review_submitted && c.status !== "finalized");
  const awaitingOther = cases.filter((c) => c.my_review_submitted && c.status !== "finalized");
  const finalized = cases.filter((c) => c.status === "finalized");

  const Section = ({ title, list, empty }) => (
    <div className="mb-10">
      <h2 className="font-display text-xl sm:text-2xl tracking-tight mb-4">{title}</h2>
      {list.length === 0 ? (
        <Card className="mano-card"><CardContent className="p-6 text-muted-foreground text-sm">{empty}</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {list.map((c) => {
            const meta = STATUS_META[c.status] || { label: c.status, tone: "bg-muted" };
            return (
              <Card key={c.case_id} className="mano-card mano-lift">
                <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="font-mono text-[10px]" data-testid={`case-id-${c.case_id.slice(0,8)}`}>
                        {c.case_id.slice(0, 8)}
                      </Badge>
                      {c.high_severity && (
                        <Badge className="bg-destructive/10 text-destructive border border-destructive/30">
                          <AlertTriangle size={12} /> High severity
                        </Badge>
                      )}
                      <span className={"text-xs px-2 py-0.5 rounded-full " + meta.tone}>{meta.label}</span>
                    </div>
                    <div className="font-display font-semibold text-lg mt-2">{c.respondent_name || "Respondent"}</div>
                    <div className="text-sm text-muted-foreground">{c.respondent_email}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Created {(c.created_at || "").replace("T", " ").slice(0, 16)} · Reviews: {c.review_count}/2
                    </div>
                  </div>
                  <Button
                    onClick={() => nav(`/clinician/cases/${c.case_id}`)}
                    className="rounded-full px-5"
                    variant={c.my_review_submitted ? "outline" : "default"}
                    data-testid={`case-open-${c.case_id.slice(0,8)}`}
                  >
                    {c.my_review_submitted ? "View case" : "Review"} <ArrowRight size={14} />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container py-10">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card px-3 py-1 text-xs text-muted-foreground">
            <Stethoscope size={12} /> Clinical Review · {user.role === "PSYCHIATRIST" ? "Psychiatrist" : "Psychologist"}
          </div>
          <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight mt-3">Case queue</h1>
          <p className="text-muted-foreground mt-2">Signed in as {user.email}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: "Pending my review", value: pending.length, tone: "text-destructive" },
            { label: "Awaiting other", value: awaitingOther.length, tone: "text-primary" },
            { label: "Finalized", value: finalized.length, tone: "text-primary" },
            { label: "Total assigned", value: cases.length, tone: "" },
          ].map((s, i) => (
            <Card key={i} className="mano-card">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</div>
                <div className={"font-display text-2xl mt-2 " + s.tone}>{s.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {fetching ? (
          <div className="text-muted-foreground">Loading cases…</div>
        ) : (
          <>
            <Section title="Pending your review" list={pending} empty="No cases waiting on you. Nice work." />
            <Section title="Awaiting counterpart" list={awaitingOther} empty="Nothing pending — your reviews are in." />
            <Section title="Finalized" list={finalized} empty="No finalized cases yet." />
          </>
        )}
      </main>
    </div>
  );
}
